// dependencies
import React from 'react'

const baseLog = (x) => (y) => Math.log(y) / Math.log(x)

const getRatio = (font, width) => {
  /*
    Original estimation based on https://gist.github.com/WhiteAbeLincoln/fa42cb698337bef14df99316c6cdffa8

    width ∝ 1/ratio
    font ∝ ratio

    bigger ratio == less text per line
  */
  const result = baseLog(10)(100 - width / font) * 0.29 - (30 - font) * 0.001

  // ensure ratio is between 0.48-0.6
  return Math.max(Math.min(result, 0.6), 0.48)
}

const getLines = ({ value, fontSize, width }) => {
  const charWidthRatio = getRatio(fontSize, width)
  const words = value.split(' ')

  let lines = ['']
  let i = 0
  let charWidth = charWidthRatio * fontSize
  let limit = width / charWidth

  // shrink font size until we're guarenteed each word will fit
  words.forEach((word) => {
    while (fontSize > 0 && word.length >= limit) {
      fontSize -= 1
      charWidth = charWidthRatio * fontSize
      limit = width / charWidth
    }
  })

  // find which words to group together on each line
  words.forEach((word) => {
    if (lines[i].length == 0 || lines[i].length + word.length < limit) {
      lines[i] += ' ' + word
      lines[i] = lines[i].trim()
    } else {
      if (word.length < limit) {
        lines[++i] = word
      } else {
        lines[++i] = word.substr(0, limit)
      }
    }
  })

  return { lines, fontSize }
}

// component
const Text = (props) => {
  const {
    value = '', // text to render
    width = 1920, // default width
    x = 0,
    y = 0,
    gRef = null, // useRef slot
    style = {}, // custom styles
    textAnchor = 'start', // anchor (start, middle, end)
    alignmentBaseline = 'hanging', // baseline, middle, hanging
    fontSize: fontSizeOriginal = 20, // size
    fontStyle = 'normal', // font style
    fontWeight = 'normal', // weight
    fontFamily = 'Open Sans', // font family
    lineHeight = 1.3, // line height
    letterSpacing = null, // letter spacing
    fill = '#333', // color
    fillOpacity = 1, // opacity
    tspanProps = {},
  } = props

  const { lines, fontSize } = getLines({ value, fontSize: fontSizeOriginal, width })

  const lineHeightPX = lineHeight * fontSize

  const getX = () => {
    if (textAnchor === 'middle') {
      return width / 2
    }
    if (textAnchor === 'end') {
      return width
    }
    return x
  }

  // compute dy values based on number of lines and alignmentBaseline setting
  // https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/alignment-baseline
  // ex: for 3 lines
  //   hanging: [0, lineHeight, 2*lineHeight]
  //   middle: [-lineHeight, 0, lineHeight]
  //   baseline: [-2*lineHeight, -1*lineHeight, 0]
  const dy = lines.map((line, index) => {
    if (alignmentBaseline == 'hanging') {
      return index * lineHeightPX
    }
    if (alignmentBaseline == 'middle') {
      return (index - (lines.length - 1) / 2) * lineHeightPX
    }
    if (alignmentBaseline == 'baseline') {
      return (index - (lines.length - 1)) * lineHeightPX
    }
  })

  // render
  return (
    <g style={style} ref={gRef}>
      <text
        alignmentBaseline={alignmentBaseline}
        fill={fill}
        fillOpacity={fillOpacity}
        fontFamily={fontFamily}
        fontSize={fontSize}
        fontStyle={fontStyle}
        fontWeight={fontWeight}
        letterSpacing={letterSpacing}
        textAnchor={textAnchor}
        x={getX()}
        y={y}
        data-width={width}
      >
        <For each="line" index="index" of={lines}>
          <tspan
            alignmentBaseline={alignmentBaseline}
            key={index}
            fontFamily={fontFamily}
            x={getX()}
            y={y}
            dx={0}
            dy={dy[index]}
            {...tspanProps}
          >
            {line}
          </tspan>
        </For>
      </text>
    </g>
  )
}

// export
export default Text
